/**
 * `KpiTilesSection` — top-row KPI grid.
 *
 * Tiles:
 *   1. Total accounts                          (snapshot.total_accounts)
 *   2. Active accounts                         (active_accounts)
 *   3. Total sites                             (total_sites)
 *   4. Transactional sites                     (transactional_sites)
 *   5. Mandates evaluated  · approval rate     (mandates_evaluated, approval_rate)
 *   6. Escalations resolved · median TTR       (escalations_resolved, median_resolution_seconds)
 *   7. Transactions                            (transactions_succeeded)
 *   8. Volume (PER CURRENCY)                   (volume[])
 *
 * The volume tile is intentionally MULTI-LINE — one row per currency
 * with `gross` headline + `net of refunds` sub — because $1 USD + $1
 * EUR is meaningless to sum (per AdminMetricsVolumeRow header). The
 * footnote pins scope: "Live-mode settlements only" — the server now
 * filters test/dry-run rows out of `volume`, so the label says so
 * plainly (CLAUDE.md §6 bounds).
 */

import type { AdminMetricsSnapshot } from '@sill/types';

import {
  formatCount,
  formatDurationSeconds,
  formatMinorCurrency,
  formatPct,
} from '../../../lib/format.js';
import { useC } from '../../../lib/theme.js';
import { KpiTile, SectionHeader } from '../KpiTile.js';

const monoFont = "'JetBrains Mono', monospace";

export type KpiTilesState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; snapshot: AdminMetricsSnapshot };

export interface KpiTilesSectionProps {
  state: KpiTilesState;
}

const TILE_MIN_HEIGHT = 132;

export function KpiTilesSection({ state }: KpiTilesSectionProps): JSX.Element {
  const C = useC();

  if (state.kind === 'loading') {
    return (
      <section>
        <SectionHeader
          kicker="Snapshot"
          title="Headline metrics"
          caption="Sums over the selected window plus live as-of-now totals."
        />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }, (_, i) => (
            <KpiTile
              key={`loading-${i}`}
              kicker="…"
              value="…"
              variant="loading"
              minHeight={TILE_MIN_HEIGHT}
            />
          ))}
        </div>
      </section>
    );
  }

  if (state.kind === 'error') {
    return (
      <section>
        <SectionHeader
          kicker="Snapshot"
          title="Headline metrics"
          caption="Sums over the selected window plus live as-of-now totals."
        />
        <div
          className="p-4"
          style={{
            background: C.surface,
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 3,
            color: C.coralFg,
            fontFamily: monoFont,
            fontSize: 12,
          }}
        >
          Snapshot unavailable: {state.message}
        </div>
      </section>
    );
  }

  const s = state.snapshot;

  return (
    <section>
      <SectionHeader
        kicker="Snapshot"
        title="Headline metrics"
        caption="Sums over the selected window plus live as-of-now totals."
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile
          kicker="Total accounts"
          value={formatCount(s.total_accounts)}
          minHeight={TILE_MIN_HEIGHT}
        />
        <KpiTile
          kicker="Active accounts"
          value={formatCount(s.active_accounts)}
          sub={
            <>
              of {formatCount(s.total_accounts)} total
            </>
          }
          minHeight={TILE_MIN_HEIGHT}
        />
        <KpiTile
          kicker="Total sites"
          value={formatCount(s.total_sites)}
          sub={
            <>
              {formatCount(s.active_sites)} active
            </>
          }
          minHeight={TILE_MIN_HEIGHT}
        />
        <KpiTile
          kicker="Transactional sites"
          value={formatCount(s.transactional_sites)}
          sub={
            <>
              of {formatCount(s.total_sites)} sites
            </>
          }
          minHeight={TILE_MIN_HEIGHT}
        />

        <KpiTile
          kicker="Mandates evaluated"
          value={formatCount(s.mandates_evaluated)}
          sub={
            <>
              approval {formatPct(s.approval_rate)} ·{' '}
              <span style={{ color: C.coralFg }}>
                {formatCount(s.mandates_rejected)} rejected
              </span>
            </>
          }
          minHeight={TILE_MIN_HEIGHT}
        />
        <KpiTile
          kicker="Escalations resolved"
          value={formatCount(s.escalations_resolved)}
          sub={
            <>
              median TTR {formatDurationSeconds(s.median_resolution_seconds)} ·{' '}
              {formatCount(s.escalations_raised)} raised
            </>
          }
          minHeight={TILE_MIN_HEIGHT}
        />
        <KpiTile
          kicker="Transactions"
          value={formatCount(s.transactions_succeeded)}
          sub="Succeeded in window"
          minHeight={TILE_MIN_HEIGHT}
        />

        <VolumeTile snapshot={s} />
      </div>
    </section>
  );
}

function VolumeTile({ snapshot }: { snapshot: AdminMetricsSnapshot }): JSX.Element {
  const C = useC();
  const rows = snapshot.volume;
  const footnote = 'Live-mode settlements only — test and dry-run excluded.';
  if (rows.length === 0) {
    return (
      <KpiTile
        kicker="Volume"
        value="—"
        sub="No settlement volume yet"
        footnote={footnote}
        minHeight={TILE_MIN_HEIGHT}
      />
    );
  }
  return (
    <KpiTile
      kicker={`Volume · ${rows.length} ${rows.length === 1 ? 'currency' : 'currencies'}`}
      value={
        <div className="flex flex-col" style={{ gap: 4 }}>
          {rows.map((r) => (
            <div key={r.currency}>
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 16,
                  color: C.text,
                  fontWeight: 500,
                  letterSpacing: -0.3,
                  lineHeight: 1.2,
                }}
              >
                {formatMinorCurrency(r.gross_minor, r.currency)}
                <span
                  style={{
                    fontSize: 10,
                    color: C.muted,
                    marginLeft: 6,
                    letterSpacing: 1,
                  }}
                >
                  {r.currency.toUpperCase()}
                </span>
              </div>
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.muted,
                  letterSpacing: 0.3,
                  lineHeight: 1.3,
                }}
              >
                net {formatMinorCurrency(r.net_minor, r.currency)}
              </div>
            </div>
          ))}
        </div>
      }
      footnote={footnote}
      minHeight={TILE_MIN_HEIGHT}
    />
  );
}
